import { useState, useEffect, useRef, useCallback } from "react";
import {
  useSearchParams,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import {
  getItems,
  getFeaturedItems,
  getStats,
} from "../../utilities/items-service";
import { itemPath } from "../../utilities/item-kinds";
import Hero from "./Hero";
import MosaicGrid from "./MosaicGrid";
import ItemModal from "../../components/ItemModal/ItemModal";

const PAGE_SIZE = 15;

const KIND_FILTERS = [
  { value: "", label: "All" },
  { value: "stamp", label: "Stamps" },
  { value: "cover", label: "First-day covers" },
  { value: "article", label: "Articles" },
];

export default function HomePage({ user }) {
  // Set when a detail route (/stamps/:catalogue_id etc.) is matched —
  // the mosaic stays mounted underneath and the item opens in a modal
  const { catalogue_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const kind = searchParams.get("kind") || "";
  const q = searchParams.get("q") || "";
  // Exact-value filter, set by clicking a series name in the item modal
  const series = searchParams.get("series") || "";

  // Active refinements, each shown as a removable chip beside the kind
  // filters. `text` reads as a phrase in the empty-state sentence.
  const refinements = [
    q && { param: "q", label: `“${q}” ×`, text: `“${q}”` },
    // Series names usually carry the word "Series" already, so the chip
    // shows the bare name
    series && {
      param: "series",
      label: `${series} ×`,
      text: `series “${series}”`,
    },
  ].filter(Boolean);

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState(null);
  const [featured, setFeatured] = useState([]);

  const sentinelRef = useRef(null);
  // False until the feed has been fetched once, so the first load doesn't
  // count as a filter change
  const feedLoadedRef = useRef(false);

  // Hero data: fetch once on mount
  useEffect(() => {
    async function fetchHeroData() {
      const [statsResponse, featuredResponse] = await Promise.all([
        getStats(),
        getFeaturedItems(),
      ]);
      if (statsResponse && !statsResponse.message) setStats(statsResponse);
      if (featuredResponse?.items) setFeatured(featuredResponse.items);
    }
    fetchHeroData();
  }, []);

  // Fetch feed page (initial or next)
  const fetchPage = useCallback(
    async (pageNum, append = false) => {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const params = new URLSearchParams();
      params.set("page", pageNum);
      params.set("limit", PAGE_SIZE);
      if (kind) params.set("kind", kind);
      if (q) params.set("q", q);
      if (series) params.set("series", series);

      const response = await getItems(params);

      if (response && response.items) {
        setItems((prev) =>
          append ? [...prev, ...response.items] : response.items,
        );
        setHasMore(response.hasMore);
        setPage(pageNum);
      } else {
        console.log("error: ", response);
      }

      if (append) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    },
    [kind, q, series],
  );

  // Initial load and filter/search changes: reset to page 1
  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMore(true);
    fetchPage(1, false);
    // A new filter usually leaves fewer tiles than the current scroll
    // position has room for — start the shortened feed at the top. Not on
    // the first load, so a reload keeps the browser's restored position.
    if (feedLoadedRef.current) window.scrollTo({ top: 0 });
    feedLoadedRef.current = true;
  }, [fetchPage]);

  // Intersection Observer for endless scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoading &&
          !isLoadingMore
        ) {
          fetchPage(page + 1, true);
        }
      },
      { rootMargin: "400px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore, page, fetchPage]);

  function selectKind(value) {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set("kind", value);
    } else {
      params.delete("kind");
    }
    setSearchParams(params, { replace: true });
  }

  function clearRefinement(param) {
    const params = new URLSearchParams(searchParams);
    params.delete(param);
    setSearchParams(params, { replace: true });
  }

  // Neighbours of the open item within the loaded feed, for prev/next
  // stepping in the modal. -1 when the item isn't in the feed (deep link
  // to something not yet loaded) — the arrows just don't show then.
  const modalIndex = catalogue_id
    ? items.findIndex((item) => item.catalogue_id === catalogue_id)
    : -1;
  const prevItem = modalIndex > 0 ? items[modalIndex - 1] : null;
  const nextItem = modalIndex !== -1 ? items[modalIndex + 1] || null : null;

  // Keep one page ahead of the modal so "next" doesn't hit a dead end
  useEffect(() => {
    if (
      modalIndex !== -1 &&
      modalIndex >= items.length - 2 &&
      hasMore &&
      !isLoading &&
      !isLoadingMore
    ) {
      fetchPage(page + 1, true);
    }
  }, [
    modalIndex,
    items.length,
    hasMore,
    isLoading,
    isLoadingMore,
    page,
    fetchPage,
  ]);

  function goToItem(item) {
    // replace so stepping through items doesn't stack up history entries,
    // and closing still returns to wherever the modal was opened from
    navigate(itemPath(item), { replace: true, state: location.state });
  }

  function closeModal() {
    if (location.state?.fromFeed) {
      navigate(-1);
    } else {
      navigate("/", { replace: true });
    }
  }

  function handleDeleted(id) {
    setItems((prev) => prev.filter((item) => item._id !== id));
    setFeatured((prev) => prev.filter((item) => item._id !== id));
  }

  return (
    <>
      <Hero stats={stats} featured={featured} />

      {/* No pb — the footer's pt sets the gap below the last tile */}
      <section className="max-w-[1287px] mx-auto px-6">
        {/* Content-type filter chips */}
        <div className="flex items-center gap-2 flex-wrap pt-[33px] pb-4 border-t border-ink/10">
          {KIND_FILTERS.map((filter) => (
            <button
              key={filter.value}
              onClick={() => selectKind(filter.value)}
              className={`px-4 py-1.5 rounded-full font-mono text-xs uppercase tracking-wider transition-colors cursor-pointer border ${
                kind === filter.value
                  ? "bg-ink text-paper border-ink"
                  : "bg-transparent text-ink/60 border-ink/20 hover:border-ink/50 hover:text-ink"
              }`}
            >
              {filter.label}
            </button>
          ))}
          {refinements.map((refinement, index) => (
            <button
              key={refinement.param}
              onClick={() => clearRefinement(refinement.param)}
              className={`${index === 0 ? "ml-2 " : ""}px-4 py-1.5 rounded-full font-mono text-xs tracking-wider bg-seal/10 text-seal border border-seal/30 hover:bg-seal/20 transition-colors cursor-pointer`}
            >
              {refinement.label}
            </button>
          ))}
        </div>

        {/* Mosaic */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader />
          </div>
        ) : items.length === 0 ? (
          <div className="flex justify-center items-center py-20">
            <p className="font-serif text-ink/50 text-base">
              {refinements.length > 0
                ? `Nothing in the archive matches ${refinements
                    .map((refinement) => refinement.text)
                    .join(" and ")}.`
                : "Nothing here yet."}
            </p>
          </div>
        ) : (
          <>
            <MosaicGrid items={items} />
            {/* Sentinel element for infinite scroll */}
            <div ref={sentinelRef} className="h-1" />
            {isLoadingMore && (
              <div className="flex justify-center items-center py-10">
                <Loader />
              </div>
            )}
          </>
        )}
      </section>

      {/* Item detail modal (deep-linkable) */}
      {catalogue_id && (
        <ItemModal
          catalogueId={catalogue_id}
          user={user}
          onClose={closeModal}
          onDeleted={handleDeleted}
          onPrev={prevItem ? () => goToItem(prevItem) : undefined}
          onNext={nextItem ? () => goToItem(nextItem) : undefined}
        />
      )}
    </>
  );
}
